import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useParams } from 'react-router-dom';

import Page from '../../../packages/common/shared-ui/src/components/Page';
import { ButtonsTypes } from '../../../packages/common/shared-ui/src/enums/commonEnums';
import DownloadButton from '../../../packages/common/shared-ui/src/icons/DownloadButton';
import EditBlueIcon from '../../../packages/common/shared-ui/src/icons/EditBlueIcon';
import FilterIcon from '../../../packages/common/shared-ui/src/icons/FilterIcon';
import ProjectEditIcon from '../../../packages/common/shared-ui/src/icons/ProjectEditIcon';
import { useTranslation } from '../../../packages/common/utils/getLocalString';

const CommonPage = ({
  children,
  buttons,
  loading,
  canCreate,
  buttonEvent,
  queryName,
  queryUserId,
  disableSave,
  downloadLoading,
  component,
  state,
  canAddBasePlan,
}) => {
  const getLocalString = useTranslation();
  const [pageTitle, setPageTitle] = useState('');
  const pageUseParams = useParams();
  const iconButtons = useRef([ButtonsTypes.DOWNLOAD]);
  const paramsPages = useRef([
    'issues',
    'timesheets',
    'confirmations',
    'agile',
    'gantt',
    'projects',
    'finances',
  ]);
  const queryPages = useRef([
    'issues',
    'timesheets',
    'confirmations',
    'agile',
    'projects',
    'finances',
  ]);
  const formPages = useRef(['create', 'edit', 'bulk_edit', 'copy']);
  const downloadPages = useRef(['issues', 'timesheets', 'gantt']);
  const saveClearParamsQueryPages = useRef(['finances', 'gantt']);
  const basePlanPages = useRef(['gantt']);
  const [currentLocation, setCurrentLocation] = useState('');
  const location = useLocation();
  const currentUser = useSelector(state => state.user.info);
  const [currentButtons, setCurrentButtons] = useState([]);
  const createButtonsMap = useRef(
    new Map([
      ['projects', { createName: 'createProject' }],
      ['issues', { createName: 'createIssue' }],
      ['timesheets', { createName: 'addSpentTime' }],
      ['agile', { createName: 'createIssue' }],
      ['gantt', { createName: 'createIssue' }],
    ]),
  );
  const queryAgileButtonsMap = useRef(
    new Map([
      ['saveQuery', { name: 'saveAgileQuery' }],
      ['editQuery', { name: 'editAgileQuery' }],
      ['deleteQuery', { name: 'deleteAgileQuery' }],
    ]),
  );

  useEffect(() => {
    let title;
    title = getLocalString(component, 'title');
    if (state && !location.pathname.includes('users')) {
      state === 'edit' && (title = getLocalString(component, 'editTitle'));
      state === 'create' && (title = getLocalString(component, 'createTitle'));
      state === 'bulk_edit' && (title = getLocalString(component, 'bulkTitle'));
    }
    setPageTitle(title);
  }, [component, getLocalString, location.pathname, state]);

  useEffect(() => {
    const pathIndex = location.pathname.indexOf('/');
    const path = location.pathname.split('/')[1];
    const primaryButtons = [
      ButtonsTypes.SAVE,
      ButtonsTypes.SAVE_AND_CONTINUE,
      ButtonsTypes.ADD,
    ];
    const withIconButtons = [ButtonsTypes.PARAMS];
    const secondaryButtons = [
      ButtonsTypes.QUERY,
      ButtonsTypes.CANCEL,
      ButtonsTypes.EDIT_QUERY,
      ButtonsTypes.DELETE_QUERY,
      ButtonsTypes.PARAMS,
      ButtonsTypes.ADD_BASE_PLAN,
    ];

    let buttons = [];
    canCreate && buttons.push(ButtonsTypes.ADD);
    //saveClearParamsQueryPages.current.some(item => path === item) &&
    //  buttons.push(ButtonsTypes.SAVE, ButtonsTypes.CANCEL);
    saveClearParamsQueryPages.current.some(item =>
      location.pathname.includes(item),
    ) && buttons.push(ButtonsTypes.SAVE, ButtonsTypes.CANCEL);
    queryPages.current.some(item => path === item) &&
      !location.pathname.includes('gantt') &&
      buttons.push(ButtonsTypes.QUERY);
    pageUseParams.queryId &&
      (currentUser.isAdmin ||
        (queryUserId && queryUserId === currentUser.id)) &&
      buttons.push(ButtonsTypes.EDIT_QUERY, ButtonsTypes.DELETE_QUERY);
    paramsPages.current.some(item => path === item) &&
      buttons.push(ButtonsTypes.PARAMS);
    //queryPages.current.some(item => path === item) && (buttons = [ButtonsTypes.QUERY, ButtonsTypes.PARAMS]);
    formPages.current.some(item => location.pathname.includes(item)) &&
      (buttons = [ButtonsTypes.SAVE, ButtonsTypes.CANCEL]);
    //pageUseParams.queryId && (buttons = [ButtonsTypes.EDIT_QUERY, ButtonsTypes.DELETE_QUERY, ButtonsTypes.PARAMS]);
    canAddBasePlan && buttons.push(ButtonsTypes.ADD_BASE_PLAN);
    downloadPages.current.some(item => location.pathname.includes(item)) &&
      !formPages.current.some(item => location.pathname.includes(item)) &&
      buttons.push(ButtonsTypes.DOWNLOAD);
    const formatButtons = buttons.reduce((acc, item) => {
      let className = ['defaultButton'];
      if (primaryButtons.includes(item)) {
        className.push('primary');
        saveClearParamsQueryPages.current.some(item =>
          location.pathname.includes(item),
        ) &&
          item === ButtonsTypes.SAVE &&
          className.push('cutRight');
      }
      if (secondaryButtons.includes(item)) {
        className.push('secondary');
        saveClearParamsQueryPages.current.some(item =>
          location.pathname.includes(item),
        ) &&
          item === ButtonsTypes.CANCEL &&
          className.push('cutLeft');
      }
      withIconButtons.includes(item) && className.push('withIcon');
      acc.push({ type: item, class: className.join(' ') });

      return acc;
    }, []);
    setCurrentButtons(formatButtons);
  }, [
    canCreate,
    currentUser.id,
    currentUser.isAdmin,
    location.pathname,
    location,
    pageUseParams.queryId,
    queryUserId,
    state,
    canAddBasePlan,
  ]);

  useEffect(() => {
    if (location) {
      setCurrentLocation(
        pageUseParams.projectId
          ? location.pathname.split('/')[3]
          : location.pathname.split('/')[1],
      );
    }
  }, [location, pageUseParams.projectId]);

  const getButtonText = useCallback(
    type => {
      let text = getLocalString('buttons', type);
      type === ButtonsTypes.ADD &&
        (text = getLocalString(
          'buttons',
          createButtonsMap.current.get(currentLocation).createName,
        ));
      location.pathname.includes('agile') &&
        queryAgileButtonsMap.current.has(type) &&
        (text = getLocalString(
          'buttons',
          queryAgileButtonsMap.current.get(type).name,
        ));

      return text;
    },
    [currentLocation, getLocalString, location.pathname],
  );

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page
          title={pageTitle}
          width='100%'
          sx={{
            height: 'calc(100vh - 80px)',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '20px',
          }}
        >
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: currentButtons.length === 0 ? '10px' : 0,
            }}
          >
            <Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='h1'
                  sx={{
                    color: '#082253',
                    fontWeight: 700,
                  }}
                >
                  {pageTitle}
                </Typography>
                {location.pathname.includes('users') && state === 'view' && (
                  <IconButton
                    disableRipple
                    onClick={() => buttonEvent(ButtonsTypes.EDIT_USER)}
                    sx={{ padding: '0', marginLeft: '16px' }}
                  >
                    <EditBlueIcon width='24' height='24' viewBox='0 0 24 24' />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            <Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center',
                }}
              >
                {currentButtons.map((item, index) => {
                  return iconButtons.current.includes(item.type) ? (
                    downloadLoading ? (
                      <Box
                        key={index}
                        sx={{
                          marginLeft: '18px',
                        }}
                      >
                        <CircularProgress
                          key={index}
                          size={24}
                          color='secondary'
                        />
                      </Box>
                    ) : (
                      <IconButton
                        key={index}
                        onClick={() => buttonEvent(item.type)}
                        disableRipple
                        className={'defaultIconButton'}
                        sx={{ marginLeft: '10px' }}
                      >
                        <DownloadButton
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                        />
                      </IconButton>
                    )
                  ) : (
                    <Button
                      disabled={
                        disableSave &&
                        (item.type === ButtonsTypes.SAVE ||
                          item.type === ButtonsTypes.SAVE_AND_CONTINUE)
                      }
                      key={index}
                      disableRipple
                      sx={{ marginLeft: index > 0 ? '10px' : 0 }}
                      className={item.class}
                      onClick={() => buttonEvent(item.type)}
                    >
                      {item.type === ButtonsTypes.PARAMS && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '4px',
                          }}
                        >
                          <FilterIcon />
                        </Box>
                      )}
                      <Typography
                        variant='h4'
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {getButtonText(item.type)}
                      </Typography>
                    </Button>
                  );
                })}
                {buttons}
              </Grid>
            </Grid>
          </Grid>
          {queryName && (
            <Typography
              variant='h2'
              sx={{
                color: '#082253',
                fontWeight: '400',
                marginTop: '20px',
              }}
            >
              {queryName}
            </Typography>
          )}
          {children}
        </Page>
      )}
    </>
  );
};

export default CommonPage;
